import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { SpecialistCard, FilterChips } from '@/components/domain';
import {
  CityPicker,
  EmptyState,
  ErrorState,
  Spinner,
  readStoredCitySlug,
} from '@/components/atoms';
import { specialists, catalog } from '@/api';
import { useBackButton } from '@/hooks';
import type { Category, SpecialistSummary } from '@/types/domain';
import { mockSpecialists } from '@/mocks/specialists';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function CategoryPage(): JSX.Element {
  useBackButton();
  const { slug = '' } = useParams<{ slug: string }>();
  const [results, setResults] = useState<SpecialistSummary[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [categoryFetchFailed, setCategoryFetchFailed] = useState(false);
  // F-180 C5: single source of truth for the city picker. Hydrate from
  // the same localStorage key SearchPage / HomePage use so the choice
  // sticks across the catalog.
  const [citySlug, setCitySlug] = useState<string | null>(() => readStoredCitySlug());
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [errored, setErrored] = useState(false);

  // F-170 B1.3 — resolve `slug` → human-readable `name_ua` for the page
  // title; otherwise the header shows the raw `accountants` slug instead
  // of "Бухгалтери" (NN/g H2 match between system and real world).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await catalog.fetchCategories();
        if (!cancelled) {
          setCategories(data.results ?? []);
          setCategoriesLoaded(true);
        }
      } catch (err) {
        logger.warn('Category fetch failed', err);
        if (!cancelled) {
          setCategoryFetchFailed(true);
          setCategoriesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const category = categories.find((c) => c.slug === slug);
  // F-180 C10: never show the raw slug. While the lookup is in flight
  // we show "Завантаження…"; if it fails or returns no match we use a
  // generic "Розділ" label and the specialist list falls back to mocks.
  const title = category?.name_ua
    ?? (categoriesLoaded ? (categoryFetchFailed ? 'Розділ' : 'Розділ') : 'Завантаження…');
  const slugNotFound = categoriesLoaded && !categoryFetchFailed && !category;

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setResults(null);
    (async () => {
      try {
        const data = await specialists.searchSpecialists({
          category: [slug],
          city: citySlug ? [citySlug] : undefined,
          verified: verifiedOnly || undefined,
          sort: 'score',
        });
        if (!cancelled) setResults(data.results);
      } catch (err) {
        logger.warn('Specialist search failed; using mocks', err);
        if (!cancelled) {
          setResults(
            mockSpecialists.filter((s) =>
              s.categories.some((c) => c.slug === slug || slug === ''),
            ),
          );
          setErrored(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, citySlug, verifiedOnly]);

  return (
    <Layout title={title}>
      <section className={styles.section}>
        <CityPicker value={citySlug} onChange={setCitySlug} />
        <FilterChips
          options={[{ value: 'verified', label: 'Тільки верифіковані' }]}
          selected={verifiedOnly ? ['verified'] : []}
          onChange={(next) => setVerifiedOnly(next.includes('verified'))}
          multi={false}
        />
      </section>

      {slugNotFound ? (
        <p className={styles.note}>
          Цього розділу немає в каталозі. Спробуй знайти спеціаліста через
          пошук або дошку.
        </p>
      ) : null}

      {results === null ? (
        <Spinner center />
      ) : results.length === 0 ? (
        <EmptyState
          icon="cat"
          title="Поки тиша"
          message="Тут ще нікого. Спробуй створити запит на дошці — і спеціалісти знайдуться."
        />
      ) : (
        <div className={styles.list}>
          {results.map((s) => (
            <SpecialistCard key={s.slug} specialist={s} />
          ))}
        </div>
      )}

      {errored && results?.length ? (
        <p className={styles.note}>Backend ще пробуджується — показані демо-дані.</p>
      ) : null}
      {errored && !results?.length ? <ErrorState onRetry={() => setResults(null)} /> : null}
    </Layout>
  );
}
